import React, { useState } from 'react';
import Seo, { SITE } from '../../components/Seo/Seo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faQuoteLeft,
	faFilm,
	faMasksTheater,
	faAward,
	faTrophy,
} from '@fortawesome/pro-regular-svg-icons';

import jessHeadshot from '../../assets/jessHeadShot.jpg';
import heatherHeadshot from '../../assets/heatherHeadShot.jpg';
import './Testimonials.css';

// Professional endorsers. Quote text is the repo's real, permissioned wording
// (kept per the guardrail); credit lines are the fact-checked block from the
// Surface-5 handoff (IBDB + Broadway Spokane). Heather's third line credits the
// PRODUCTION's 5 Tony wins, not a personal producer win — verified correction.
const PROS = [
	{
		name: 'Jessica Keenan Wynn',
		role: 'Broadway & Film',
		photo: jessHeadshot,
		initials: 'JK',
		quote:
			"When working with Grace, you'll not only acquire great technique, but you'll gain a mentor, a confidant, and a cheerleader. It's this formula that sets Grace apart from other teachers. She will encourage you in ways that will benefit what you apply in the classroom and also in life outside of it. If we all had a little more Grace in our lives, we'd be much better off! I highly and wholeheartedly recommend Grace!",
		credits: [
			{ icon: faFilm, text: 'Mamma Mia! Here We Go Again (Legendary Pictures)' },
			{ icon: faMasksTheater, text: 'Beautiful (Broadway)' },
			{ icon: faMasksTheater, text: 'Heathers: The Musical (Off-Broadway)' },
		],
	},
	{
		name: 'Heather Shields',
		role: 'Broadway Producer',
		photo: heatherHeadshot,
		initials: 'HS',
		quote:
			'I have worked with Grace in a number of capacities as a producer and manager over the years. Beyond her immense talent as a performer, for which I have had the pleasure to hire her many times, I find myself consistently turning to her as a top-rated instructor and coach. I have had the privilege of working with several of her students - all of whom are talented, polished, and professional. I have also had the privilege to hire Grace as a teaching artist for several projects. I know that when Grace is on the team, the students she is working with will produce joyful, confident, and top-rate performances. I simply cannot recommend her enough!',
		credits: [
			{ icon: faAward, text: 'Cabaret with Eddie Redmayne (Tony nomination)' },
			{ icon: faAward, text: "The Sign in Sidney Brustein's Window (Tony nomination)" },
			{ icon: faTrophy, text: 'A Christmas Carol (Broadway) — 5-time Tony-winning production' },
		],
	},
];

// Parent & student testimonials. Quote text verbatim from the repo's store;
// initials avatars (no photos). Roles per the handoff.
const PARENTS = [
	{
		name: 'Karen Nelson',
		role: 'Parent',
		initials: 'KN',
		quote:
			'My daughter, Eliana, has grown so much as a singer, performer and overall human since working with Grace and Anthony at Color Your Voice. With their support, guidance and instruction, Eliana realized her dream of pursuing a college degree in Musical Theatre. Through their ongoing coaching, she was fully prepared to audition for some of the top programs in Musical Theater. Eliana was accepted into multiple prestigious programs, and has begun her journey at one of the top Musical Theatre programs in the Nation. We can not recommend Grace and Anthony enough! They are simply amazing!',
	},
	{
		name: 'Amy Crnecki',
		role: 'Parent · 4 years',
		initials: 'AC',
		quote:
			"We've had the absolute joy of working with Grace and Anthony for the past four years, and I truly can't say enough about how incredible they are. Their talent, warmth, passion, and kindness shine through in everything they do. They've not only helped my daughter grow tremendously as a singer, but they've also sparked a genuine love for music and the performing arts that she'll carry with her forever. She always looks forward to her lessons—they make learning so much fun, and instill many important life lessons that span far beyond the stage! Grace and Anthony have become like family to us, and we're endlessly grateful to have them in our lives.",
	},
	{
		name: 'Robin Richards',
		role: 'Mom of Rowen',
		initials: 'RR',
		quote:
			'Color Your Voice is so much more than helping my daughter embrace her passion for music and singing - it is about giving her confidence and focus to use her VOICE in all the ways she can. Her ability to step onto a stage and sing has translated into her confidence at school in asking questions, making friends, volunteering to help and even - speak at all school assemblies. She is stepping into the leader and advocate that she is meant to be - and voice lessons with recitals have been a significant part of that development.',
	},
	{
		name: 'Tara Nelson',
		role: 'Parent',
		initials: 'TN',
		quote:
			"Calvin has been working with Grace and now Anthony since their first triple threat camp when they were 9. Since that time, it's been so fun to see the growth not only in Calvin, but the rest of the incredible students. Not only have they shown tremendous growth in their singing abilities, but their confidence, and support for one another. These wonderful humans (Grace and Anthony) not only teach so much about singing, showmanship, stage presence, acting, auditions and more, but they embrace each and every student, meet them where they're at, and encourage them to be their truest self. We've seen Calvin go from literally hiding behind a pole to auditioning for and landing leading roles in musicals.",
	},
	{
		name: 'Tammy Field',
		role: 'Parent',
		initials: 'TF',
		quote:
			'Anthony and Grace have been such a blessing! They helped my daughters come out of their shells, find their voices, and gain so much confidence. With their guidance, the girls are now shining in their shows. If you want coaches who truly care, choose Anthony and Grace!',
	},
	{
		name: 'Marisa',
		role: 'Parent',
		initials: 'M',
		quote:
			'Grace has given my 14-year-old son the confidence to take risks and take his musical theatre journey to the next level. His dedication has significantly increased since working with Grace. He trusts her and feels comfortable reaching out to her when he might need an additional lesson to prepare for an audition or show. Lessons via FaceTime have been a lifesaver (both before and during pandemic) for us as a family. One less place to run to and in the comfort of our home. We are so thankful to have found Grace!',
	},
];

const FILTERS = [
	{ key: 'all', label: 'All' },
	{ key: 'pro', label: 'Professionals' },
	{ key: 'parent', label: 'Parents & students' },
];

// Headshot with a graceful fallback: if the image fails to load, render the
// initials avatar instead of a broken image (handoff requirement).
const ProAvatar = ({ photo, name, initials }) => {
	const [failed, setFailed] = useState(false);
	if (failed) {
		return <span className='q__av q__av--ini'>{initials}</span>;
	}
	return <img className='q__av' src={photo} alt={name} onError={() => setFailed(true)} />;
};

const Testimonials = () => {
	const [group, setGroup] = useState('all');
	const showPros = group === 'all' || group === 'pro';
	const showParents = group === 'all' || group === 'parent';

	return (
		<main className='cyv testimonials-page'>
			<Seo
				title='Kind Words | Color Your Voice'
				description='From Broadway stages to school assemblies — what artists, producers, and parents say about working with Grace and Anthony at Color Your Voice.'
				canonical={`${SITE}/testimonials`}
			/>

			{/* ---- Hero (centered) ---- */}
			<header className='pg-hero'>
				<span className='eyebrow'>Kind words</span>
				<h1 className='brand-heading'>From our studio family</h1>
				<p>
					From Broadway stages to school assemblies — what artists, producers,
					and parents say about working with Grace and Anthony.
				</p>
			</header>

			<div className='tw'>
				{/* ---- Filter chips (centered, React state) ---- */}
				<div className='tw-filter' role='group' aria-label='Filter testimonials'>
					{FILTERS.map((f) => (
						<button
							key={f.key}
							type='button'
							className={`tw-chip${group === f.key ? ' is-on' : ''}`}
							aria-pressed={group === f.key}
							onClick={() => setGroup(f.key)}>
							{f.label}
						</button>
					))}
				</div>

				{/* ---- Professionals ---- */}
				{showPros && (
					<section className='tw-group'>
						<p className='tw-group__label'>Broadway &amp; industry</p>
						<div className='tw-pros'>
							{PROS.map((p) => (
								<figure className='q q--feature' key={p.name}>
									<FontAwesomeIcon icon={faQuoteLeft} className='q__mark' />
									<blockquote className='q__text'>{p.quote}</blockquote>
									<figcaption className='q__by'>
										<ProAvatar photo={p.photo} name={p.name} initials={p.initials} />
										<span className='q__who'>
											<span className='q__name'>{p.name}</span>
											<span className='q__role'>{p.role}</span>
										</span>
									</figcaption>
									<ul className='q__credits'>
										{p.credits.map((c, i) => (
											<li key={i}>
												<FontAwesomeIcon icon={c.icon} />
												<span>{c.text}</span>
											</li>
										))}
									</ul>
								</figure>
							))}
						</div>
					</section>
				)}

				{/* ---- Parents & students ---- */}
				{showParents && (
					<section className='tw-group'>
						<p className='tw-group__label'>Parents &amp; students</p>
						<div className='tw-parents'>
							{PARENTS.map((p) => (
								<figure className='q' key={p.name}>
									<FontAwesomeIcon icon={faQuoteLeft} className='q__mark' />
									<blockquote className='q__text'>{p.quote}</blockquote>
									<figcaption className='q__by'>
										<span className='q__av q__av--ini'>{p.initials}</span>
										<span className='q__who'>
											<span className='q__name'>{p.name}</span>
											<span className='q__role'>{p.role}</span>
										</span>
									</figcaption>
								</figure>
							))}
						</div>
					</section>
				)}
			</div>
		</main>
	);
};

export default Testimonials;
